import { SoftwarePackageServiceCodes } from "libs/api/gateways/field/field-ops-gateway.dtos";
import { FieldDeviceStatus } from "modules/field-mgmt/tabs/FieldDeviceStatus";
import React from "react";

import { DATE_FORMATS } from "@bps/date-time";
import {
  ButtonsGroupChoice,
  ButtonsGroupOption,
  Heading,
  MessageBar,
  MessageBarType,
  Stack,
  Text,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import {
  FieldDeviceConfigurationComponent,
  FieldDeviceReportedConfig
} from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { useFieldDeviceReportedConfig } from "@libs/api/gateways/field/field-ops-gateway.hooks";

interface SoftwarePackageHeaderProps {
  reportedConfig: FieldDeviceReportedConfig | undefined;
  desiredConfig: FieldDeviceConfigurationComponent | undefined;
  selectedVersion: SelectedVersionState;
  setSelectedVersion: (version: SelectedVersionState) => void;
  fieldDeviceId: string;
}

const REALTIME_ENABLE_KEY = "RealtimeCommandsEnabled";

export enum SelectedVersionState {
  ReportedConfig = "reportedConfig",
  DesiredConfig = "desiredConfig"
}

export const SoftwarePackageHeader: React.FC<SoftwarePackageHeaderProps> = ({
  reportedConfig,
  desiredConfig,
  selectedVersion,
  setSelectedVersion,
  fieldDeviceId
}) => {
  const theme = useTheme();

  const { data: deviceReportedConfig } = useFieldDeviceReportedConfig(
    fieldDeviceId
  );

  if (!reportedConfig && !desiredConfig) {
    return <Text variant="xLarge">Software Package Not Found</Text>;
  }

  const realtimeEnablement = deviceReportedConfig
    ?.find(
      config =>
        config.softwarePackageCode ===
        SoftwarePackageServiceCodes.FieldController
    )
    ?.configurationSettings.find(val => val.key === REALTIME_ENABLE_KEY);

  const realtimeDisabledToolTip = (
    <>
      This device last reported its real-time enablement status as disabled. To
      enable real-time communications, include{" "}
      <code style={{ backgroundColor: theme.palette.neutralLight }}>
        RealtimeCommandsEnabled=true
      </code>{" "}
      in an applicable rollout.
    </>
  );

  const versionOptions: ButtonsGroupOption<string>[] = [
    ...(reportedConfig?.softwarePackageVersion
      ? [
          {
            key: SelectedVersionState.ReportedConfig,
            text: "", // Replace by onRender
            onRenderText: () => (
              <Stack>
                <Text variant="tiny">Current Version</Text>
                <Text variant="medium">
                  {reportedConfig?.softwarePackageVersion}
                </Text>
              </Stack>
            )
          }
        ]
      : []),
    ...(desiredConfig?.version
      ? [
          {
            key: SelectedVersionState.DesiredConfig,
            text: "", // Replace by onRender
            onRenderText: () => (
              <Stack>
                <Text variant="tiny">Desired Version</Text>
                <Text variant="medium">{desiredConfig?.version}</Text>
              </Stack>
            )
          }
        ]
      : [])
  ];

  return (
    <Stack
      styles={{
        root: {
          borderColor: theme.semanticColors.variantBorder,
          borderWidth: 1,
          borderStyle: "solid",
          padding: theme.spacing.s1
        }
      }}
    >
      <Stack horizontal horizontalAlign="space-between">
        <Stack verticalFill verticalAlign="space-between">
          <Text variant="xLarge">
            {reportedConfig?.softwarePackageDisplayName ?? desiredConfig?.name}
          </Text>
          <Stack
            horizontal
            tokens={{ childrenGap: theme.spacing.s1 }}
            styles={{ root: { alignItems: "flex-end" } }}
          >
            <Stack.Item>
              <Heading labelPaddings>Command target version</Heading>
              <ButtonsGroupChoice
                notUnselectable
                options={versionOptions}
                onChange={(version: SelectedVersionState) =>
                  setSelectedVersion(version)
                }
                value={selectedVersion}
              />
            </Stack.Item>

            {realtimeEnablement?.value !== "true" && (
              <TooltipHost content={realtimeDisabledToolTip}>
                <MessageBar messageBarType={MessageBarType.warning}>
                  Realtime disabled
                </MessageBar>
              </TooltipHost>
            )}
          </Stack>
        </Stack>
        <Stack
          verticalFill
          verticalAlign="space-between"
          styles={{ root: { height: "fit-content" } }}
        >
          <FieldDeviceStatus
            status={reportedConfig?.status ?? reportedConfig?.runStatus}
          />
          <Text variant="small">
            Last Observed:{" "}
            {reportedConfig?.lastObservedDateTimeUtc.toFormat(
              DATE_FORMATS.LONG_DATE_FORMAT_MONTH_AHEAD_CUT
            )}
          </Text>
        </Stack>
      </Stack>
    </Stack>
  );
};
